/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `VirtualEventForm` for the Caintra Connect frontend.
 * @module pages/VirtualEventForm
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../utils/logger.js";
import styles from "../styles/VirtualEventForm.module.css";
import LanguageSelector from "../components/LanguageSelector/index.jsx";
import NotificationBell from "../components/NotificationBell/index.jsx";
import { AppContext } from "../context/AppContext";
import {
  generateAssetUrl,
  validateBusinessEventName,
} from "../utils/common.js";
import { canManageVirtualEvents } from "../utils/accessControl";
import useRequireAccess from "../hooks/useRequireAccess";
import {
  submitVirtualEvent,
  initializeVirtualEventForm,
  getAllVirtualEvents,
} from "../logic/virtualEvents";
import { Button, CircularProgress, Switch, Backdrop } from "@mui/material";

import { ReactComponent as SaveIcon } from "../assets/icons/CompanyEditPage/SaveIcon.svg";
import { ReactComponent as UploadIcon } from "../assets/icons/Cloud_Upload.svg";
import { ReactComponent as TitleIcon } from "../assets/icons/EventCreation/TitleIcon.svg";
import { ReactComponent as ChevronLeftIcon } from "../assets/icons/EventCreation/ChevronLeftIcon.svg";

import { use, useRef, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import AssetImage from "../components/AssetImage";

const DATE_FORMAT = "YYYY-MM-DD";

const DURATION_INPUT_PATTERN = /^([0-9]|1[0-9]|2[0-4]):[0-5][0-9]$/;

/** @param {string|null|undefined} value */
const trimDuration = (value) => {
  if (value == null) {
    return null;
  }
  const s = String(value).trim();
  return s === "" ? null : s;
};

/** @param {string} s */
const parseDurationMinutes = (s) => {
  if (!DURATION_INPUT_PATTERN.test(s)) {
    return null;
  }
  const [h, m] = s.split(":").map(Number);
  return h * 60 + m;
};

/** @param {unknown} audiencia */
const isAudienciaChosen = (audiencia) =>
  audiencia === 0 ||
  audiencia === 1 ||
  audiencia === 2 ||
  audiencia === "0" ||
  audiencia === "1" ||
  audiencia === "2";

/** @param {unknown} value */
const isNotificacionPreviaChosen = (value) =>
  value === 0 || value === 1 || value === "0" || value === "1";

/** @param {unknown} costo */
const isCostFilled = (costo) => {
  if (costo === null || costo === undefined) {
    return false;
  }
  const s = String(costo).trim();
  if (s === "") {
    return false;
  }
  const n = Number(s);
  return Number.isFinite(n) && n >= 0;
};

const VirtualEventForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { changePermissions } = use(AppContext);
  const { allowed } = useRequireAccess(canManageVirtualEvents);

  const [formData, setFormData] = useState({
    imagenBanner: null,
    nombre: "",
    fechaEvento: null,
    descripcion: "",
    hora: null,
    duracion: null,
    audiencia: null,
    fechaDisponibleSocios: null,
    fechaDisponibleNoSocios: null,
    fechaLimiteRegistro: null,
    tiempoMinimoAsistencia: null,
    notificacionPrevia: null,
    costoSocios: null,
    costoNoSocios: null,
    activo: true,
  });
  const [showError, setShowError] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [imageChanged, setImageChanged] = useState(false);
  const [imageFormat, setImageFormat] = useState(null);
  const [imageName, setImageName] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [eventData, setEventData] = useState(null);
  const [allVirtualEvents, setAllVirtualEvents] = useState([]);
  const [nameValidation, setNameValidation] = useState({ isValid: true });

  const imageRef = useRef(null);

  const onDragEnter = () => imageRef.current?.classList.add(styles.dragOver);
  const onDragLeave = () => imageRef.current?.classList.remove(styles.dragOver);
  const onDrop = () => imageRef.current?.classList.remove(styles.dragOver);

  useEffect(() => {
    if (!allowed) {
      return;
    }
    if (location.state?.isEditMode && location.state?.eventData) {
      setIsEditMode(true);
      setEventData(location.state.eventData);
    } else {
      setIsEditMode(false);
      setEventData(null);
    }

    const initializeComponent = async () => {
      const token = localStorage.getItem("token");
      if (!token || token.trim() === "undefined") {
        return;
      }
      const userId = localStorage.getItem("userId");

      const allEvents = await getAllVirtualEvents(navigate);
      setAllVirtualEvents(allEvents);

      await initializeVirtualEventForm(
        location.state?.eventData?._id,
        userId,
        changePermissions,
        navigate,
        dayjs,
        setFormData,
        setLoading,
      );
    };

    initializeComponent();
  }, [allowed, location.state, navigate, location.pathname]);

  useEffect(() => {
    if (!String(formData.nombre ?? "").trim()) {
      setNameValidation({ isValid: true });
      return;
    }
    if (allVirtualEvents.length > 0) {
      const validation = validateBusinessEventName(
        formData.nombre,
        allVirtualEvents,
        eventData?._id,
      );
      setNameValidation(validation);
    }
  }, [allVirtualEvents, formData.nombre, eventData?._id]);

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    const maxSize = 5 * 1024 * 1024;
    if (!allowedTypes.includes(file.type)) {
      setError(t("pages.buyerCompanyForm.logoFileTypeError"));
      setImageChanged(false);
      setFormData((prev) => ({ ...prev, imagenBanner: null }));
      setImageName(null);
      setImageFormat(null);
      return;
    }
    if (file.size > maxSize) {
      setError(t("pages.buyerCompanyForm.logoFileSizeError"));
      setImageChanged(false);
      setFormData((prev) => ({ ...prev, imagenBanner: null }));
      setImageFormat(null);
      setImageName(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      setImageChanged(true);
      setFormData((prev) => ({ ...prev, imagenBanner: e.target.result }));
      setImageName(file.name);
      setImageFormat(file.type);
      setHasChanges(true);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowError(false);
    try {
      const eventToSubmit = {
        ...formData,
        nombreImagen: imageChanged ? imageName : null,
        formatoImagen: imageChanged ? imageFormat : null,
        userId: localStorage.getItem("userId"),
        fechaEvento: dayjs(formData.fechaEvento).format(DATE_FORMAT),
        fechaDisponibleSocios: formData.fechaDisponibleSocios
          ? dayjs(formData.fechaDisponibleSocios).format(DATE_FORMAT)
          : null,
        fechaDisponibleNoSocios: formData.fechaDisponibleNoSocios
          ? dayjs(formData.fechaDisponibleNoSocios).format(DATE_FORMAT)
          : null,
        fechaLimiteRegistro: formData.fechaLimiteRegistro
          ? dayjs(formData.fechaLimiteRegistro).format(DATE_FORMAT)
          : null,
      };
      const result = await submitVirtualEvent(
        eventToSubmit,
        eventData?._id,
        navigate,
        setSaving,
        setEventData,
        setHasChanges,
      );
      if (result?.ok && result.wasCreate) {
        navigate("/virtualEvents", { state: { eventCreated: true } });
      } else if (result?.ok && eventData?._id) {
        navigate("/virtualEvents", { state: { eventUpdated: true } });
      }
    } catch (err) {
      logger.error("Error", { err: sanitizeError(err) });
      setShowError(true);
    }
  };

  /** Same required fields for create and edit (`activo` is optional). */
  const isVirtualEventFormComplete = () => {
    const durStr = trimDuration(formData.duracion);
    const durMinutes =
      durStr != null && DURATION_INPUT_PATTERN.test(durStr)
        ? parseDurationMinutes(durStr)
        : null;

    const minStr = trimDuration(formData.tiempoMinimoAsistencia) ?? "0:00";
    const minMinutes = DURATION_INPUT_PATTERN.test(minStr)
      ? parseDurationMinutes(minStr)
      : null;

    return (
      Boolean(formData.imagenBanner) &&
      String(formData.nombre ?? "").trim() !== "" &&
      Boolean(formData.fechaEvento) &&
      Boolean(formData.hora) &&
      String(formData.descripcion ?? "").trim() !== "" &&
      isAudienciaChosen(formData.audiencia) &&
      Boolean(formData.fechaDisponibleSocios) &&
      Boolean(formData.fechaDisponibleNoSocios) &&
      Boolean(formData.fechaLimiteRegistro) &&
      durMinutes != null &&
      durMinutes > 0 &&
      minMinutes != null &&
      minMinutes >= 0 &&
      isCostFilled(formData.costoSocios) &&
      isCostFilled(formData.costoNoSocios) &&
      isNotificacionPreviaChosen(formData.notificacionPrevia)
    );
  };

  const checkSaveDisabled = () => {
    if (saving || loading || !nameValidation.isValid) {
      return true;
    }
    if (isEditMode) {
      return !hasChanges || !isVirtualEventFormComplete();
    }
    return !isVirtualEventFormComplete();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "nombre" && allVirtualEvents.length > 0) {
      const validation = validateBusinessEventName(
        value,
        allVirtualEvents,
        eventData?._id,
      );
      setNameValidation(validation);
    }
    setHasChanges(true);
  };

  /** @param {"socios"|"noSocios"} which */
  const handleAudienciaCheckboxChange = (which) => {
    setFormData((prev) => {
      const aud = prev.audiencia;
      const sociosWas = aud === 0 || aud === "0" || aud === 2 || aud === "2";
      const noSociosWas = aud === 1 || aud === "1" || aud === 2 || aud === "2";
      const socios = which === "socios" ? !sociosWas : sociosWas;
      const noSocios = which === "noSocios" ? !noSociosWas : noSociosWas;
      let audiencia = null;
      if (socios && noSocios) {
        audiencia = 2;
      } else if (socios) {
        audiencia = 0;
      } else if (noSocios) {
        audiencia = 1;
      }
      return { ...prev, audiencia };
    });
    setHasChanges(true);
  };

  if (!allowed) {
    return null;
  }

  return (
    <div className={styles.container}>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        }}
        open={loading || saving}
      >
        <CircularProgress size={60} thickness={4} sx={{ color: "#4eabe7" }} />
      </Backdrop>

      <div className={styles.header}>
        <div className={styles.languages}>
          <NotificationBell navigate={navigate} />
          <LanguageSelector />
        </div>
        <div className={styles.backNavigation}>
          <button
            type="button"
            className={styles.backButton}
            onClick={() => navigate(-1)}
          >
            <ChevronLeftIcon />
            <span>{t("common.goBack")}</span>
          </button>
        </div>
        <div className={styles.headerInfoContainer}>
          <div className={styles.headerInfo}>
            <TitleIcon className={styles.titleIcon} />
            <h1 className={styles.title}>
              {t("pages.virtualEventForm.header")}
            </h1>
          </div>
        </div>
      </div>

      <div className={styles.formContainer}>
        <form onSubmit={handleSubmit} className={styles.eventForm}>
          <div className={styles.formGroups}>
            <div className={styles.formGroupOne}>
              <div className={styles.formGroupOneLeftStack}>
                <div className={styles.imageContainer}>
                  <label className={styles.label}>
                    {t("pages.virtualEventForm.eventBanner")}
                  </label>
                  <div
                    ref={imageRef}
                    className={styles.dropFileInput}
                    onDragEnter={onDragEnter}
                    onDragLeave={onDragLeave}
                    onDrop={onDrop}
                  >
                    {isEditMode || formData.imagenBanner ? (
                      <AssetImage
                        className={styles.uploadedImage}
                        src={generateAssetUrl(formData.imagenBanner)}
                        alt=""
                      />
                    ) : (
                      <div className={styles.uploadContainer}>
                        <UploadIcon className={styles.uploadIcon} />
                        {error && (
                          <span className={styles.errorMessage}>{error}</span>
                        )}
                        <span className={styles.uploadTitle}>
                          {t("pages.virtualEventForm.drag&drop")}
                        </span>
                        <span className={styles.uploadSubtitle}>
                          {t("pages.virtualEventForm.yourImage")}{" "}
                          <span
                            style={{ color: "#1D5AE8" }}
                            className={styles.uploadSubtitle}
                          >
                            {t("pages.virtualEventForm.browseFile")}
                          </span>
                        </span>
                      </div>
                    )}
                    <input
                      className={styles.imageInput}
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                  </div>
                </div>
                <div className={styles.audienciaBelowBanner}>
                  <label className={styles.label}>
                    {t("pages.virtualEventForm.audience")}
                  </label>
                  <div className={styles.audienciaCheckboxRow}>
                    <label className={styles.audienciaCheckboxLabel}>
                      <input
                        type="checkbox"
                        checked={
                          formData.audiencia === 0 ||
                          formData.audiencia === "0" ||
                          formData.audiencia === 2 ||
                          formData.audiencia === "2"
                        }
                        onChange={() => handleAudienciaCheckboxChange("socios")}
                      />
                      <span>{t("pages.virtualEventForm.audienceSocios")}</span>
                    </label>
                    <label className={styles.audienciaCheckboxLabel}>
                      <input
                        type="checkbox"
                        checked={
                          formData.audiencia === 1 ||
                          formData.audiencia === "1" ||
                          formData.audiencia === 2 ||
                          formData.audiencia === "2"
                        }
                        onChange={() =>
                          handleAudienciaCheckboxChange("noSocios")
                        }
                      />
                      <span>
                        {t("pages.virtualEventForm.audienceNonMembers")}
                      </span>
                    </label>
                  </div>
                </div>
                <div className={styles.activoBelowBanner}>
                  <label className={styles.label}>
                    {t("pages.virtualEventForm.activateEvent")}
                  </label>
                  <Switch
                    checked={Boolean(formData.activo)}
                    onChange={(e) => {
                      setHasChanges(true);
                      setFormData((prev) => ({
                        ...prev,
                        activo: e.target.checked,
                      }));
                    }}
                  />
                </div>
              </div>
              <div className={styles.detailsContainer}>
                <div className={`${styles.formRow} ${styles.rowEventName}`}>
                  <div className={styles.nameCol}>
                    <label className={styles.label}>
                      {t("pages.virtualEventForm.eventName")}
                    </label>
                    <input
                      type="text"
                      name="nombre"
                      value={formData.nombre}
                      onChange={handleInputChange}
                      className={`${styles.input} ${(showError && !formData.nombre) || !nameValidation.isValid ? styles.inputError : ""}`}
                      placeholder={t(
                        "pages.virtualEventForm.eventNamePlaceholder",
                      )}
                    />
                    {!nameValidation.isValid && (
                      <div className={styles.validationMessage}>
                        {t("pages.virtualEventForm.duplicateEventNameError")}
                      </div>
                    )}
                  </div>
                </div>

                <div className={`${styles.formRow} ${styles.rowEventType}`}>
                  <div className={styles.eventDateCol}>
                    <label className={styles.label}>
                      {t("pages.virtualEventForm.eventDate")}
                    </label>
                    <input
                      type="date"
                      name="fechaEvento"
                      className={`${styles.input} ${showError && !formData.fechaEvento ? styles.inputError : ""}`}
                      value={formData.fechaEvento || ""}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className={styles.dateDivider} />
                  <div className={styles.eventDateCol}>
                    <label className={styles.label}>
                      {t("pages.virtualEventForm.eventTime")}
                    </label>
                    <input
                      type="time"
                      name="hora"
                      value={formData.hora || ""}
                      onChange={handleInputChange}
                      className={styles.input}
                    />
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.descriptionCol}>
                    <label className={styles.label}>
                      {t("pages.virtualEventForm.description")}
                    </label>
                    <textarea
                      name="descripcion"
                      value={formData.descripcion ?? ""}
                      onChange={handleInputChange}
                      className={`${styles.textarea} ${showError && !formData.descripcion ? styles.textareaError : ""}`}
                      placeholder={t(
                        "pages.virtualEventForm.descriptionPlaceholder",
                      )}
                    />
                  </div>
                </div>

                <div className={`${styles.formRow} ${styles.rowThreeDateCols}`}>
                  <div className={styles.eventDateCol}>
                    <label className={styles.label}>
                      {t("pages.virtualEventForm.membersDate")}
                    </label>
                    <input
                      type="date"
                      name="fechaDisponibleSocios"
                      className={styles.input}
                      value={formData.fechaDisponibleSocios || ""}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div
                    className={`${styles.eventDateCol} ${styles.nonMembersDateCol}`}
                  >
                    <label className={styles.label}>
                      {t("pages.virtualEventForm.nonMembersDate")}
                    </label>
                    <input
                      type="date"
                      name="fechaDisponibleNoSocios"
                      className={styles.input}
                      value={formData.fechaDisponibleNoSocios || ""}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className={styles.eventDateCol}>
                    <label className={styles.label}>
                      {t("pages.virtualEventForm.dueDateRegistration")}
                    </label>
                    <input
                      type="date"
                      name="fechaLimiteRegistro"
                      className={styles.input}
                      value={formData.fechaLimiteRegistro || ""}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className={`${styles.formRow} ${styles.rowTwoCols}`}>
                  <div className={styles.durationCol}>
                    <label className={styles.label}>
                      {t("pages.virtualEventForm.duration")}
                    </label>
                    <div className={styles.durationInputContainer}>
                      <input
                        type="text"
                        name="duracion"
                        pattern="([0-9]|1[0-9]|2[0-4]):[0-5][0-9]"
                        value={formData.duracion || "0:00"}
                        onChange={handleInputChange}
                        className={styles.input}
                        placeholder={t("common.durationPlaceholder")}
                      />
                      <span className={styles.durationUnit}>
                        {t("pages.b2bEventForm.hoursShort")}
                      </span>
                    </div>
                  </div>
                  <div className={styles.durationCol}>
                    <label className={styles.label}>
                      {t("pages.virtualEventForm.minAttendance")}
                    </label>
                    <div className={styles.durationInputContainer}>
                      <input
                        type="text"
                        name="tiempoMinimoAsistencia"
                        pattern="([0-9]|1[0-9]|2[0-4]):[0-5][0-9]"
                        value={formData.tiempoMinimoAsistencia || "0:00"}
                        onChange={handleInputChange}
                        className={styles.input}
                        placeholder={t("common.durationPlaceholder")}
                      />
                      <span className={styles.durationUnit}>
                        {t("pages.b2bEventForm.hoursShort")}
                      </span>
                    </div>
                  </div>
                </div>

                <div className={`${styles.formRow} ${styles.rowTwoCols}`}>
                  <div className={styles.durationCol}>
                    <label className={styles.label}>
                      {t("pages.b2bEventForm.membersCost")}
                    </label>
                    <input
                      type="number"
                      name="costoSocios"
                      min="0"
                      step="0.01"
                      value={
                        formData.costoSocios === null ||
                        formData.costoSocios === undefined
                          ? ""
                          : formData.costoSocios
                      }
                      onChange={handleInputChange}
                      className={styles.input}
                      placeholder={t("pages.b2bEventForm.costPlaceholder")}
                    />
                  </div>
                  <div className={styles.durationCol}>
                    <label className={styles.label}>
                      {t("pages.b2bEventForm.nonMembersCost")}
                    </label>
                    <input
                      type="number"
                      name="costoNoSocios"
                      min="0"
                      step="0.01"
                      value={
                        formData.costoNoSocios === null ||
                        formData.costoNoSocios === undefined
                          ? ""
                          : formData.costoNoSocios
                      }
                      onChange={handleInputChange}
                      className={styles.input}
                      placeholder={t("pages.b2bEventForm.costPlaceholder")}
                    />
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.durationCol}>
                    <label className={styles.label}>
                      {t("pages.virtualEventForm.advanceNotice")}
                    </label>
                    <div className={styles.notificacionPreviaRadioRow}>
                      <label className={styles.audienciaCheckboxLabel}>
                        <input
                          type="radio"
                          name="notificacionPrevia"
                          checked={
                            formData.notificacionPrevia === 0 ||
                            formData.notificacionPrevia === "0"
                          }
                          onChange={() => {
                            setHasChanges(true);
                            setFormData((prev) => ({
                              ...prev,
                              notificacionPrevia: 0,
                            }));
                          }}
                        />
                        <span>
                          {t("pages.virtualEventForm.advanceNoticeYes")}
                        </span>
                      </label>
                      <label className={styles.audienciaCheckboxLabel}>
                        <input
                          type="radio"
                          name="notificacionPrevia"
                          checked={
                            formData.notificacionPrevia === 1 ||
                            formData.notificacionPrevia === "1"
                          }
                          onChange={() => {
                            setHasChanges(true);
                            setFormData((prev) => ({
                              ...prev,
                              notificacionPrevia: 1,
                            }));
                          }}
                        />
                        <span>
                          {t("pages.virtualEventForm.advanceNoticeNo")}
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.saveSectionContainer}>
            <div className={styles.saveSectionContent}>
              <div className={styles.messageContainer}>
                {t("pages.virtualEventForm.publishInstructions")}
              </div>
              <Button
                type="submit"
                variant="contained"
                className={styles.saveButton}
                disabled={checkSaveDisabled()}
                startIcon={loading ? null : <SaveIcon />}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  t("pages.virtualEventForm.publishEvent")
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VirtualEventForm;
